var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, observable, runInAction } from "mobx";
import { slugify } from "healthex-common/utils";
import { getObservableT, i18nGetCurrentLanguage, i18nGetDateFnsLocale } from "../Infra/i18next";
import { Disposer } from "../Infra/Disposer";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { HXMenuController } from "./HXMenu";
import { FILTER_ALL, FILTER_GROUP_PROCEDURE_DIAGNOSTIC, FILTER_GROUP_VISITS, HistoryCategory, VISIT_TYPE_TO_CATEGORY, PROCEDURE_TYPE_TO_CATEGORY, visitTypeDisplay, procedureTypeDisplay, safeParse, formatDisplayDate, provenanceSourceDisplay, } from "./PatientHistory.types";
import { printScreen } from "./printScreen";
import { HealthWalletDetailsRecordType, HealthWalletRecordType, ProvenanceSource, VisitType, ProcedureType, TIMELINE_TYPE_ALL, } from "healthex-common/health.wallet.dtos";
import { FeatureFlag } from "healthex-common/features.dtos";
import { PatientDataVisualizationPdfStatus } from "healthex-common/patient.data.visualization.dtos";
import { openAsyncWindow } from "./WindowHelpers";
import { differenceInMonths, format } from "date-fns";
const t = getObservableT("patients");
export { FILTER_ALL, FILTER_GROUP_VISITS, FILTER_GROUP_PROCEDURE_DIAGNOSTIC } from "./PatientHistory.types";
export var PatientHistoryContentView;
(function (PatientHistoryContentView) {
    PatientHistoryContentView["LOADING"] = "LOADING";
    PatientHistoryContentView["PROCESSING"] = "PROCESSING";
    PatientHistoryContentView["EMPTY"] = "EMPTY";
    PatientHistoryContentView["HOME_CONTENT"] = "HOME_CONTENT";
    PatientHistoryContentView["NO_MATCHES"] = "NO_MATCHES";
    PatientHistoryContentView["DATA"] = "DATA";
})(PatientHistoryContentView || (PatientHistoryContentView = {}));
function buildFilterGroups() {
    return [
        {
            id: FILTER_GROUP_VISITS,
            label: t("Visits"),
            options: [
                { id: FILTER_ALL, label: t("All") },
                { id: "primary-care", label: t("Primary Care") },
                { id: "specialist", label: t("Specialist") },
                { id: "urgent-care", label: t("Urgent Care") },
                { id: "emergency", label: t("Emergency") },
                { id: "annual-physical", label: t("Annual Physical") },
                { id: "mental-health", label: t("Mental Health") },
                { id: "dental", label: t("Dental") },
                { id: "vision", label: t("Vision") },
                { id: "chiropractic-pt", label: t("Chiropractic / PT") },
                { id: "alternative", label: t("Alternative") },
                { id: "obstetrics", label: t("Obstetrics") },
            ],
        },
        {
            id: FILTER_GROUP_PROCEDURE_DIAGNOSTIC,
            label: t("Procedure & Diagnostic"),
            options: [
                { id: FILTER_ALL, label: t("All") },
                { id: "lab-draw", label: t("Lab Draw") },
                { id: "imaging", label: t("Imaging") },
                { id: "biopsy", label: t("Biopsy") },
                { id: "colonoscopy-endoscopy", label: t("Colonoscopy / Endoscopy") },
                { id: "infusion", label: t("Infusion / IV") },
                { id: "minor-procedure", label: t("Minor Procedure") },
                { id: "surgery", label: t("Surgery") },
                { id: "vaccination", label: t("Vaccination") },
                { id: "genetic-test", label: t("Genetic Test") },
                { id: "sleep-study", label: t("Sleep Study") },
                { id: "allergy-testing", label: t("Allergy Testing") },
                { id: "cardiac-stress-test", label: t("Cardiac Stress Test") },
                { id: "pulmonary-function-test", label: t("Pulmonary Function") },
            ],
        },
    ];
}
const VISIT_TYPE_FILTER_ID = {
    [VisitType.PRIMARY_CARE]: "primary-care",
    [VisitType.SPECIALIST]: "specialist",
    [VisitType.URGENT_CARE]: "urgent-care",
    [VisitType.EMERGENCY]: "emergency",
    [VisitType.ANNUAL_PHYSICAL]: "annual-physical",
    [VisitType.MENTAL_HEALTH]: "mental-health",
    [VisitType.PSYCHIATRY]: "mental-health",
    [VisitType.DENTAL]: "dental",
    [VisitType.VISION]: "vision",
    [VisitType.PHYSICAL_THERAPY]: "chiropractic-pt",
    [VisitType.ALTERNATIVE_CARE]: "alternative",
    [VisitType.OBSTETRICS]: "obstetrics",
};
const PROCEDURE_TYPE_FILTER_ID = {
    [ProcedureType.LAB_DRAW]: "lab-draw",
    [ProcedureType.IMAGING]: "imaging",
    [ProcedureType.BIOPSY]: "biopsy",
    [ProcedureType.COLONOSCOPY_ENDOSCOPY]: "colonoscopy-endoscopy",
    [ProcedureType.INFUSION]: "infusion",
    [ProcedureType.MINOR_PROCEDURE]: "minor-procedure",
    [ProcedureType.EVALUATION_AND_MANAGEMENT]: null,
    [ProcedureType.SURGERY]: "surgery",
    [ProcedureType.VACCINATION]: "vaccination",
    [ProcedureType.GENETIC_TEST]: "genetic-test",
    [ProcedureType.SLEEP_STUDY]: "sleep-study",
    [ProcedureType.ALLERGY_TESTING]: "allergy-testing",
    [ProcedureType.CARDIAC_STRESS_TEST]: "cardiac-stress-test",
    [ProcedureType.PULMONARY_FUNCTION_TEST]: "pulmonary-function-test",
};
function invertFilterMap(coverage) {
    var _a;
    const result = {};
    for (const key of Object.keys(coverage)) {
        const enumValue = key;
        const filterId = coverage[enumValue];
        if (filterId === null)
            continue;
        const bucket = (_a = result[filterId]) !== null && _a !== void 0 ? _a : [];
        bucket.push(enumValue);
        result[filterId] = bucket;
    }
    return result;
}
const VISIT_FILTER_TO_API = invertFilterMap(VISIT_TYPE_FILTER_ID);
const PROCEDURE_FILTER_TO_API = invertFilterMap(PROCEDURE_TYPE_FILTER_ID);
function isGapDto(dto) {
    return dto.type === HealthWalletRecordType.GAP;
}
function formatMonthYear(raw) {
    const d = safeParse(raw);
    if (!d)
        return "";
    const locale = i18nGetDateFnsLocale();
    return format(d, "LLL yyyy", locale ? { locale } : undefined);
}
function computeGapMonths(startRaw, endRaw) {
    const a = safeParse(startRaw);
    const b = safeParse(endRaw);
    if (!a || !b)
        return 0;
    return Math.max(1, Math.abs(differenceInMonths(b, a)));
}
function mapRecordDto(dto) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    let category;
    let subtype;
    if (dto.type === HealthWalletRecordType.VISIT) {
        category = (_b = VISIT_TYPE_TO_CATEGORY[(_a = dto.visitType) !== null && _a !== void 0 ? _a : ""]) !== null && _b !== void 0 ? _b : HistoryCategory.PRIMARY_CARE;
        subtype = visitTypeDisplay((_c = dto.visitType) !== null && _c !== void 0 ? _c : "");
    }
    else {
        category = (_e = PROCEDURE_TYPE_TO_CATEGORY[(_d = dto.procedureType) !== null && _d !== void 0 ? _d : ""]) !== null && _e !== void 0 ? _e : HistoryCategory.MINOR_PROCEDURE;
        subtype = procedureTypeDisplay((_f = dto.procedureType) !== null && _f !== void 0 ? _f : "");
    }
    const parsed = safeParse((_g = dto.date) !== null && _g !== void 0 ? _g : null);
    const year = parsed ? String(parsed.getFullYear()) : t("Unknown");
    const recordType = dto.type === HealthWalletRecordType.VISIT ? HealthWalletDetailsRecordType.VISIT : HealthWalletDetailsRecordType.PROCEDURE;
    return {
        id: dto.id,
        recordType,
        category,
        title: (_j = (_h = dto.name) !== null && _h !== void 0 ? _h : dto.description) !== null && _j !== void 0 ? _j : "",
        subtype,
        date: formatDisplayDate((_k = dto.date) !== null && _k !== void 0 ? _k : null),
        year,
        source: (_m = (_l = dto.providerName) !== null && _l !== void 0 ? _l : dto.practiceName) !== null && _m !== void 0 ? _m : provenanceSourceDisplay(ProvenanceSource.CLINICAL_RECORDS),
    };
}
function deriveGapId(dto) {
    return `gap-${dto.nextRecord.id}-${dto.previousRecord.id}`;
}
function mapGapDto(dto, connectedSources) {
    var _a, _b, _c, _d, _e;
    const startParsed = safeParse((_a = dto.previousRecord.date) !== null && _a !== void 0 ? _a : null);
    return {
        id: deriveGapId(dto),
        durationMonths: computeGapMonths((_b = dto.previousRecord.date) !== null && _b !== void 0 ? _b : null, (_c = dto.nextRecord.date) !== null && _c !== void 0 ? _c : null),
        startDate: formatMonthYear((_d = dto.previousRecord.date) !== null && _d !== void 0 ? _d : null),
        endDate: formatMonthYear((_e = dto.nextRecord.date) !== null && _e !== void 0 ? _e : null),
        year: startParsed ? String(startParsed.getFullYear()) : t("Unknown"),
        connectedSourcesCount: connectedSources,
    };
}
function extractYear(item) {
    return item.data.year;
}
function mapDtoToTimelineItem(dto, connectedSources) {
    if (isGapDto(dto)) {
        return { type: "gap", data: mapGapDto(dto, connectedSources) };
    }
    return { type: "record", data: mapRecordDto(dto) };
}
export class PatientHistoryViewModel {
    constructor(container) {
        this.fetchSequence = 0;
        this.dismissDialogOpen = false;
        this.selectedDismissReasons = new Set();
        this.isPrinting = false;
        this.isDownloadingPdf = false;
        this.detailsDialogOpen = false;
        this.detailsDialogRecordId = null;
        this.detailsDialogRecordType = null;
        this.isLoading = true;
        this.isFilterLoading = false;
        this.isLoadingMore = false;
        this.records = [];
        this.serverTotal = 0;
        this.serverHasMore = false;
        this.nextOffset = 0;
        this.connectedSources = 0;
        this.initialFetchTotal = null;
        this.counts = null;
        this.dismissedGapIds = new Set();
        this.dismissingGapId = null;
        this.filterSheetOpen = false;
        this.moreMenuController = new HXMenuController();
        this.disposer = new Disposer();
        this.wasProcessing = false;
        this.routingUIManager = container.routingUIManager;
        this.dashboardUIManager = container.patientResearchDashboardUIManager;
        this.healthWalletService = container.healthWalletService;
        this.authManager = container.authManager;
        this.featureManager = container.featureManager;
    }
    get isHealthWalletEnabled() {
        return this.featureManager.isFeatureEnabled(FeatureFlag.RO_HEALTH_WALLET_ENABLED);
    }
    get contentView() {
        if (this.isLoading || this.isUnifiedRecordNotLoaded)
            return PatientHistoryContentView.LOADING;
        if (this.isUnifiedRecordProcessing)
            return PatientHistoryContentView.PROCESSING;
        if (!this.isHealthWalletEnabled) {
            return this.hasHomeContent ? PatientHistoryContentView.HOME_CONTENT : PatientHistoryContentView.EMPTY;
        }
        if (!this.hasDatasetRecords)
            return PatientHistoryContentView.EMPTY;
        if (this.totalItemCount === 0 && !this.isFilterLoading)
            return PatientHistoryContentView.NO_MATCHES;
        return PatientHistoryContentView.DATA;
    }
    get hasHomeContent() {
        return this.recordsSummary !== null || this.hasUnifiedRecordExpired === true;
    }
    get homeHeadlinePrimary() {
        return t("Hi,");
    }
    get homeHeadlineSecondary() {
        return this.authManager.firstName;
    }
    get homeHeroDescription() {
        return t("See your health history, all in one secure document.");
    }
    get name() {
        return this.authManager.name;
    }
    get hasUnifiedRecordExpired() {
        return this.dashboardUIManager.hasUnifiedRecordExpired;
    }
    get recordsSummary() {
        if (this.hasUnifiedRecordExpired === true)
            return null;
        const summary = this.dashboardUIManager.unifiedRecordSummary;
        if (summary === NOT_LOADED)
            return null;
        return summary;
    }
    get mostRecentRetrieval() {
        return this.dashboardUIManager.mostRecentRetrieval;
    }
    get shouldShowUnifiedRecordButton() {
        if (this.mostRecentRetrieval === NOT_LOADED)
            return false;
        if (!this.mostRecentRetrieval)
            return false;
        const retrieval = this.mostRecentRetrieval;
        if (!retrieval.finalized) {
            return false;
        }
        if (retrieval.finalized && retrieval.pdfStatus !== PatientDataVisualizationPdfStatus.COMPLETED) {
            return false;
        }
        return true;
    }
    get primaryCallToAction() {
        if (this.hasUnifiedRecordExpired === true || this.isUnifiedRecordProcessing) {
            return { label: t("Get my latest records"), onClick: () => this.getUpdatedRecords() };
        }
        else if (this.isDownloadPdfEnabled && this.shouldShowUnifiedRecordButton) {
            return {
                label: t("See my HealthEx Unified Record"),
                longPressLabel: t("See my raw records"),
                onClick: (rawRecordsMode) => this.showUnifiedRecord(rawRecordsMode ? "raw" : "pdf"),
            };
        }
        return { label: t("Get my HealthEx Unified Record"), onClick: () => this.getRecords() };
    }
    get secondaryCallToAction() {
        if (this.hasUnifiedRecordExpired === true || this.isUnifiedRecordProcessing) {
            return null;
        }
        else if (this.isDownloadPdfEnabled) {
            return { label: t("Get my latest records"), onClick: () => this.getUpdatedRecords() };
        }
        return null;
    }
    get isPrimaryCallToActionDisabled() {
        return this.isUnifiedRecordProcessing;
    }
    showUnifiedRecord(type = "pdf") {
        void openAsyncWindow(async () => {
            var _a, _b;
            const url = type === "raw"
                ? (_a = (await this.dashboardUIManager.getDownloadRawDocumentsLink())) === null || _a === void 0 ? void 0 : _a.downloadLink
                : (_b = (await this.dashboardUIManager.getDownloadPdfLink())) === null || _b === void 0 ? void 0 : _b.downloadLink;
            if (!url) {
                console.warn(`No URL available`);
                return null;
            }
            return url;
        }).catch((e) => {
            console.error("[PatientHistory] Failed to open unified record", e);
        });
    }
    getRecords() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_CONSENT);
    }
    getUpdatedRecords() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_DATA_UPDATE);
    }
    get filterGroups() {
        return buildFilterGroups();
    }
    run() {
        this.disposer.reaction(() => this.featureManager.hasLoadedFeatures, (hasLoaded) => {
            if (hasLoaded)
                void this.fetchTimeline(true);
        }, { name: "fetchTimelineOnceFeaturesLoaded", fireImmediately: true });
        this.disposer.reaction(() => this.dashboardUIManager.isUnifiedRecordProcessing, (isProcessing) => {
            if (this.wasProcessing && isProcessing !== true) {
                this.initialFetchTotal = null;
                this.counts = null;
                void this.fetchTimeline(true);
            }
            this.wasProcessing = isProcessing === true;
        }, { name: "refetchOnProcessingComplete", fireImmediately: true });
    }
    stop() {
        this.disposer.dispose();
        this.closeDetailsDialog();
    }
    buildApiParams() {
        var _a, _b;
        const params = {};
        const topRowAll = this.dashboardUIManager.selectedTopRowFilters.includes(FILTER_ALL);
        const wantVisits = topRowAll || this.dashboardUIManager.selectedTopRowFilters.includes(FILTER_GROUP_VISITS);
        const wantProcedures = topRowAll || this.dashboardUIManager.selectedTopRowFilters.includes(FILTER_GROUP_PROCEDURE_DIAGNOSTIC);
        if (wantVisits) {
            const visitSelected = (_a = this.dashboardUIManager.selectedFiltersByGroup.get(FILTER_GROUP_VISITS)) !== null && _a !== void 0 ? _a : [FILTER_ALL];
            if (visitSelected.includes(FILTER_ALL)) {
                params.visitType = [TIMELINE_TYPE_ALL];
            }
            else {
                const apiValues = [...new Set(visitSelected.flatMap((id) => { var _a; return (_a = VISIT_FILTER_TO_API[id]) !== null && _a !== void 0 ? _a : []; }))];
                if (apiValues.length > 0) {
                    params.visitType = apiValues;
                }
            }
        }
        if (wantProcedures) {
            const procSelected = (_b = this.dashboardUIManager.selectedFiltersByGroup.get(FILTER_GROUP_PROCEDURE_DIAGNOSTIC)) !== null && _b !== void 0 ? _b : [FILTER_ALL];
            if (procSelected.includes(FILTER_ALL)) {
                params.procedureType = [TIMELINE_TYPE_ALL];
            }
            else {
                const apiValues = [...new Set(procSelected.flatMap((id) => { var _a; return (_a = PROCEDURE_FILTER_TO_API[id]) !== null && _a !== void 0 ? _a : []; }))];
                if (apiValues.length > 0) {
                    params.procedureType = apiValues;
                }
            }
        }
        return params;
    }
    async fetchTimeline(reset) {
        if (!this.isHealthWalletEnabled) {
            this.isLoading = false;
            return;
        }
        const patientId = this.authManager.id;
        if (!patientId)
            return;
        if (!reset && this.isLoadingMore)
            return;
        const seq = ++this.fetchSequence;
        const isInitialLoad = this.initialFetchTotal === null;
        if (reset && isInitialLoad) {
            this.isLoading = true;
            this.records = [];
            this.serverTotal = 0;
            this.serverHasMore = false;
        }
        else if (reset) {
            this.isFilterLoading = true;
        }
        else {
            this.isLoadingMore = true;
        }
        try {
            const offset = reset ? 0 : this.nextOffset;
            const filterParams = this.buildApiParams();
            const response = await this.healthWalletService.getTimeline(patientId, {
                offset,
                includeGaps: true,
                includeCounts: this.counts === null,
                ...filterParams,
            });
            if (seq !== this.fetchSequence)
                return;
            runInAction(() => {
                if (reset) {
                    this.records = response.records;
                }
                else {
                    this.records = [...this.records, ...response.records];
                }
                if (response.counts !== undefined) {
                    if (this.initialFetchTotal === null) {
                        this.initialFetchTotal = response.counts.total;
                    }
                    this.serverTotal = response.counts.total;
                    this.counts = response.counts;
                }
                this.nextOffset = response.offset + response.limit;
                this.serverHasMore = response.hasMore;
                if (response.connectedSources !== undefined) {
                    this.connectedSources = response.connectedSources;
                }
                this.isLoading = false;
                this.isLoadingMore = false;
                this.isFilterLoading = false;
            });
        }
        catch (error) {
            if (seq !== this.fetchSequence)
                return;
            console.error("[PatientHistory] Failed to fetch timeline", error);
            runInAction(() => {
                this.isLoading = false;
                this.isFilterLoading = false;
                this.isLoadingMore = false;
            });
        }
    }
    get timelineItems() {
        i18nGetCurrentLanguage();
        const sources = this.connectedSources;
        return this.records
            .map((dto) => mapDtoToTimelineItem(dto, sources))
            .filter((item) => {
            if (item.type === "gap")
                return !this.dismissedGapIds.has(item.data.id);
            return true;
        });
    }
    get totalItemCount() {
        return this.timelineItems.length;
    }
    get hasMoreItems() {
        return this.serverHasMore;
    }
    get timelineYears() {
        var _a;
        const byYear = new Map();
        for (const item of this.timelineItems) {
            const year = extractYear(item);
            const bucket = (_a = byYear.get(year)) !== null && _a !== void 0 ? _a : [];
            bucket.push(item);
            byYear.set(year, bucket);
        }
        return Array.from(byYear.entries())
            .sort(([a], [b]) => {
            const numA = parseInt(a, 10);
            const numB = parseInt(b, 10);
            if (Number.isNaN(numA))
                return 1;
            if (Number.isNaN(numB))
                return -1;
            return numB - numA;
        })
            .map(([year, items]) => ({ year, items }));
    }
    get hasRecords() {
        return this.timelineItems.length > 0 && this.timelineItems.some((item) => item.type === "record");
    }
    get hasDatasetRecords() {
        var _a;
        return ((_a = this.initialFetchTotal) !== null && _a !== void 0 ? _a : 0) > 0;
    }
    get isUnifiedRecordNotLoaded() {
        return this.dashboardUIManager.isUnifiedRecordDownloadable === NOT_LOADED;
    }
    get isUnifiedRecordProcessing() {
        return this.dashboardUIManager.isUnifiedRecordProcessing === true;
    }
    get processingStateText() {
        return t("We’re still pulling your records. This might take a few minutes. We’ll notify you when we’re done.");
    }
    get refreshRecordsLabel() {
        if (this.isUnifiedRecordProcessing)
            return t("Refresh Records");
        if (!this.isHealthWalletEnabled) {
            return this.hasHomeContent ? t("Refresh Records") : t("Find my Records");
        }
        return this.hasDatasetRecords ? t("Refresh Records") : t("Find my Records");
    }
    get isDownloadPdfEnabled() {
        return this.dashboardUIManager.isUnifiedRecordDownloadable === true;
    }
    loadMoreItems() {
        if (!this.hasMoreItems || this.isLoadingMore || this.isFilterLoading)
            return;
        void this.fetchTimeline(false);
    }
    isFilterOptionEnabled(optionId) {
        if (optionId === FILTER_ALL)
            return true;
        if (!this.counts)
            return false;
        if (optionId in VISIT_FILTER_TO_API) {
            return VISIT_FILTER_TO_API[optionId].some((vt) => { var _a; return ((_a = this.counts.visitTypes[vt]) !== null && _a !== void 0 ? _a : 0) > 0; });
        }
        if (optionId in PROCEDURE_FILTER_TO_API) {
            return PROCEDURE_FILTER_TO_API[optionId].some((pt) => { var _a; return ((_a = this.counts.procedureTypes[pt]) !== null && _a !== void 0 ? _a : 0) > 0; });
        }
        return false;
    }
    selectedFiltersFor(groupId) {
        var _a;
        return (_a = this.dashboardUIManager.selectedFiltersByGroup.get(groupId)) !== null && _a !== void 0 ? _a : [FILTER_ALL];
    }
    onFilterIconClick() {
        this.filterSheetOpen = true;
    }
    closeFilterSheet() {
        this.filterSheetOpen = false;
    }
    toggleFilter(groupId, optionId) {
        var _a;
        const filters = this.dashboardUIManager.selectedFiltersByGroup;
        const current = (_a = filters.get(groupId)) !== null && _a !== void 0 ? _a : [FILTER_ALL];
        if (optionId === FILTER_ALL) {
            filters.set(groupId, [FILTER_ALL]);
        }
        else if (current.includes(optionId)) {
            const next = current.filter((id) => id !== optionId);
            filters.set(groupId, next.length > 0 ? next : [FILTER_ALL]);
        }
        else {
            const next = current.filter((id) => id !== FILTER_ALL);
            next.push(optionId);
            filters.set(groupId, next);
        }
        void this.fetchTimeline(true);
    }
    isTopRowFilterSelected(filterId) {
        return this.dashboardUIManager.selectedTopRowFilters.includes(filterId);
    }
    toggleTopRowFilter(filterId) {
        const topRow = this.dashboardUIManager.selectedTopRowFilters;
        const alreadySelected = topRow.length === 1 && topRow[0] === filterId;
        if (alreadySelected) {
            if (filterId === FILTER_ALL)
                return;
            this.dashboardUIManager.selectedTopRowFilters = [FILTER_ALL];
        }
        else {
            this.dashboardUIManager.selectedTopRowFilters = [filterId];
        }
        void this.fetchTimeline(true);
    }
    onFindMyRecords() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_CONSENT, { step: "welcome" });
    }
    onRefreshRecords() {
        this.moreMenuController.closeMenu();
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_CONSENT, { step: "welcome" });
    }
    async onPrintRecords() {
        if (this.isPrinting)
            return;
        if (!this.hasDatasetRecords)
            return;
        this.isPrinting = true;
        try {
            if (this.hasMoreItems) {
                await this.fetchAllRemainingRecords();
                this.moreMenuController.closeMenu();
                await new Promise((resolve) => requestAnimationFrame(resolve));
            }
            else {
                this.moreMenuController.closeMenu();
            }
            await printScreen({});
        }
        catch (error) {
            console.error("[PatientHistory] Failed to print records", error);
            this.moreMenuController.closeMenu();
        }
        finally {
            runInAction(() => {
                this.isPrinting = false;
            });
        }
    }
    async fetchAllRemainingRecords() {
        const patientId = this.authManager.id;
        if (!patientId)
            return;
        const seq = ++this.fetchSequence;
        this.isLoadingMore = true;
        const filterParams = this.buildApiParams();
        try {
            const response = await this.healthWalletService.getTimeline(patientId, {
                offset: this.nextOffset,
                limit: this.serverTotal - this.nextOffset,
                includeGaps: true,
                ...filterParams,
            });
            if (seq !== this.fetchSequence)
                return;
            runInAction(() => {
                this.records = [...this.records, ...response.records];
                this.nextOffset = response.offset + response.limit;
                this.serverHasMore = response.hasMore;
            });
        }
        finally {
            runInAction(() => {
                this.isLoadingMore = false;
            });
        }
    }
    async onDownloadPdf() {
        if (!this.isDownloadPdfEnabled || this.isDownloadingPdf)
            return;
        this.isDownloadingPdf = true;
        try {
            await this.dashboardUIManager.openUnifiedRecordDownloadInNewWindow("pdf", {
                onBeforeFetch: () => {
                    runInAction(() => {
                        this.moreMenuController.closeMenu();
                    });
                },
                onNoDownloadLink: () => {
                    window.alert(t("We couldn't open a download link for your records. Please try again in a moment."));
                },
            });
        }
        catch (e) {
            console.error("[PatientHistory] Failed to download PDF", e);
        }
        finally {
            runInAction(() => {
                this.isDownloadingPdf = false;
            });
        }
    }
    onSearchRecords() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_CONSENT, { step: "welcome" });
    }
    onRecordClick(id, recordType) {
        this.detailsDialogRecordId = id;
        this.detailsDialogRecordType = slugify(recordType);
        this.detailsDialogOpen = true;
    }
    closeDetailsDialog() {
        this.detailsDialogOpen = false;
        this.detailsDialogRecordId = null;
        this.detailsDialogRecordType = null;
    }
    openDismissDialog(gapId) {
        this.selectedDismissReasons.clear();
        this.dismissingGapId = gapId;
        this.dismissDialogOpen = true;
    }
    closeDismissDialog() {
        this.dismissDialogOpen = false;
        this.dismissingGapId = null;
    }
    toggleDismissReason(reasonId) {
        if (this.selectedDismissReasons.has(reasonId)) {
            this.selectedDismissReasons.delete(reasonId);
        }
        else {
            this.selectedDismissReasons.add(reasonId);
        }
    }
    async saveDismissReasons() {
        if (!this.dismissingGapId || this.selectedDismissReasons.size === 0)
            return;
        const gapId = this.dismissingGapId;
        const gapDto = this.records.find((r) => isGapDto(r) && deriveGapId(r) === gapId);
        if (!gapDto || !isGapDto(gapDto))
            return;
        const patientId = this.authManager.id;
        if (!patientId)
            return;
        const body = {
            previousRecord: gapDto.previousRecord,
            nextRecord: gapDto.nextRecord,
            reasons: [...this.selectedDismissReasons],
        };
        this.dismissedGapIds.add(gapId);
        this.dismissDialogOpen = false;
        this.dismissingGapId = null;
        try {
            await this.healthWalletService.dismissGap(patientId, body);
            runInAction(() => {
                const index = this.records.findIndex((r) => isGapDto(r) && deriveGapId(r) === gapId);
                if (index !== -1) {
                    this.records.splice(index, 1);
                }
                this.dismissedGapIds.delete(gapId);
            });
        }
        catch (error) {
            console.error("[PatientHistory] Failed to dismiss gap", error);
            runInAction(() => {
                this.dismissedGapIds.delete(gapId);
            });
        }
    }
}
__decorate([
    observable
], PatientHistoryViewModel.prototype, "dismissDialogOpen", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "selectedDismissReasons", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "isPrinting", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "isDownloadingPdf", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "detailsDialogOpen", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "detailsDialogRecordId", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "detailsDialogRecordType", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "isLoading", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "isFilterLoading", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "isLoadingMore", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "records", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "serverTotal", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "serverHasMore", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "nextOffset", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "connectedSources", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "initialFetchTotal", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "counts", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "dismissedGapIds", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "dismissingGapId", void 0);
__decorate([
    observable
], PatientHistoryViewModel.prototype, "filterSheetOpen", void 0);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "isHealthWalletEnabled", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "contentView", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "hasHomeContent", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "homeHeadlinePrimary", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "homeHeadlineSecondary", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "homeHeroDescription", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "name", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "hasUnifiedRecordExpired", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "recordsSummary", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "mostRecentRetrieval", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "shouldShowUnifiedRecordButton", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "primaryCallToAction", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "secondaryCallToAction", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "isPrimaryCallToActionDisabled", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "getRecords", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "getUpdatedRecords", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "filterGroups", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "fetchTimeline", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "timelineItems", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "totalItemCount", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "hasMoreItems", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "timelineYears", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "hasRecords", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "hasDatasetRecords", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "isUnifiedRecordNotLoaded", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "isUnifiedRecordProcessing", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "processingStateText", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "refreshRecordsLabel", null);
__decorate([
    computed
], PatientHistoryViewModel.prototype, "isDownloadPdfEnabled", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "loadMoreItems", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "onFilterIconClick", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "closeFilterSheet", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "toggleFilter", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "toggleTopRowFilter", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "onFindMyRecords", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "onRefreshRecords", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "onPrintRecords", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "fetchAllRemainingRecords", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "onDownloadPdf", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "onSearchRecords", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "onRecordClick", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "closeDetailsDialog", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "openDismissDialog", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "closeDismissDialog", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "toggleDismissReason", null);
__decorate([
    action
], PatientHistoryViewModel.prototype, "saveDismissReasons", null);
